/** @jsxImportSource @emotion/react */
import { useEffect } from "react";

import { useLocation } from "react-router";

import { useDetailsStore } from "@/stores/useDetailsStore";
import { useDrawerStore } from "@/stores/useDrawerStore";
import { useMainBannerStore } from "@/stores/useMainBannerStore";
import { useTableStoreByRoute } from "@/stores/useTableStore";
import { css } from "@emotion/react";

import { endOfBetaVersionDate } from "../../Pages/Auth/constants";
import {
  desktopDetailsSectionWidth,
  ROUTES,
  useIsFutureTime,
  useMediaQueries
} from "../../shared";
import { routesWithoutDetails } from "../../shared/functions/functions";
import useGeneralHeights from "../../shared/hooks/useGeneralHeights";
import usePreviousLocation from "../../shared/hooks/usePreviousLocation";
import {
  SECTION_OPEN_DELAY_NOT_RESET,
  SECTION_OPEN_DELAY_ON_RESET
} from "../DetailsSection/constants";
import MainInfoBanner from "../MainInfoBanner/MainInfoBanner";
import Navbar from "../Navbar/Navbar";
import {
  containerWithDetailsStyle,
  containerWithoutDetailsStyle
} from "./styleFunctions";

interface MainContainerProps {
  children: React.ReactNode;
}

const MainContainer = ({ children, ...props }: MainContainerProps) => {
  const { toMd, toLg, fromMd } = useMediaQueries();
  const location = useLocation();
  const prevLocation: any = usePreviousLocation(location);
  const { isFutureTime } = useIsFutureTime(endOfBetaVersionDate);

  const resetDetailsSection = useDetailsStore(
    state => state.resetDetailsSection
  );
  const setResetDetailsSection = useDetailsStore(
    state => state.setResetDetailsSection
  );

  // Get main banner height from store
  const mainBannerHeight = useMainBannerStore(state => state.height);

  // Get drawer state from store
  const isDesktopDrawerOpen = useDrawerStore(
    state => state.isDesktopDrawerOpen
  );

  const {
    OPEN_DESKTOP_DRAWER_WIDTH_WITH_PADDING,
    CLOSE_DESKTOP_DRAWER_WIDTH_WITH_PADDING
  } = useGeneralHeights(mainBannerHeight);

  // Get table store based on current route
  const { data: tableRowData, params } = useTableStoreByRoute(
    location.pathname as ROUTES
  );

  const isDesktopDetailsSectionOpen = params.desktopDetailsSection;

  const delay =
    resetDetailsSection && isDesktopDetailsSectionOpen
      ? SECTION_OPEN_DELAY_ON_RESET
      : SECTION_OPEN_DELAY_NOT_RESET;

  useEffect(() => {
    // check if delay on open details section is needed
    if (prevLocation && prevLocation.pathname !== location.pathname) {
      setResetDetailsSection(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, setResetDetailsSection]);

  const generateMarginLeft: any = () => {
    if (!fromMd) {
      return "0";
    }
    if (!isDesktopDrawerOpen) {
      return CLOSE_DESKTOP_DRAWER_WIDTH_WITH_PADDING;
    }
    return OPEN_DESKTOP_DRAWER_WIDTH_WITH_PADDING;
  };

  const generateMarginRight = () => {
    if (!routesWithoutDetails(location.pathname)) {
      if (toMd) {
        return "0";
      }
      if (isDesktopDetailsSectionOpen && tableRowData.length > 0) {
        return desktopDetailsSectionWidth(toLg);
      } else {
        return "0";
      }
    }
    return "unset";
  };

  return (
    <>
      {isFutureTime && <MainInfoBanner />}
      <Navbar route={location.pathname as ROUTES} />
      {routesWithoutDetails(location.pathname) ? (
        <main
          {...props}
          className="remaining-height"
          css={css(containerWithoutDetailsStyle(generateMarginLeft))}
        >
          {children}
        </main>
      ) : (
        <main
          {...props}
          className="remaining-height"
          css={css(
            containerWithDetailsStyle(
              generateMarginLeft,
              generateMarginRight,
              delay
            )
          )}
        >
          {children}
        </main>
      )}
    </>
  );
};

export default MainContainer;
